import React from "react";
// ANT DESIGN COMPONENTS
import { Tag } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import { RootState } from "../../app/store";
// FLATBUFFER DISPLAY
import { cognitoUserStatusLabel, userStatusLabel } from "../../flatbuffers/display/enums";
// STATUS METADATA
import { ALL_COGNITO_STATUSES, COGNITO_STATUS_META } from "./cognitoStatusMeta";
import { ALL_USER_STATUSES, USER_STATUS_META, userStatusIcon } from "./userStatusMeta";

interface UserStatusLegendProps {
  /** Heading above the rows; omit for none. */
  title?: string;
}

/**
 * Legend for every user status, rendered from the generated `UserStatus`
 * enum so it cannot fall behind the schema: a new member appears here the
 * moment it is generated.
 *
 * The tag colours are the SAME ones the Users table paints, taken from the
 * one shared map — a legend whose colours drift from the table it explains
 * is worse than no legend.
 */
function UserStatusLegend({ title = "What the statuses mean" }: UserStatusLegendProps) {
  const { theme } = useSelector((state: RootState) => state.home);

  return (
    <div
      className={`drawer__box__${theme}`}
      style={{ padding: "16px" }}
      data-testid="user-status-legend"
    >
      {title !== "" && <h6 className="generalStyles__mrFix">{title}</h6>}
      <div className="container">
        {ALL_USER_STATUSES.map((status) => {
          const meta = USER_STATUS_META[status];
          return (
            <div className="row" key={status} style={{ marginTop: 8 }}>
              <div className="col-md-3">
                <Tag
                  color={meta.color}
                  icon={
                    <FontAwesomeIcon
                      icon={userStatusIcon(status)}
                      className="generalStyles__mrFix"
                    />
                  }
                >
                  {userStatusLabel(status)}
                </Tag>
              </div>
              <div className="col-md-9">
                <span className="drawer__text">{meta.description}</span>
              </div>
            </div>
          );
        })}
      </div>
      <h6 className="generalStyles__mrFix" style={{ marginTop: 16 }}>
        Sign-in account (live from Cognito)
      </h6>
      <div className="container" data-testid="cognito-status-legend">
        {ALL_COGNITO_STATUSES.map((status) => {
          const meta = COGNITO_STATUS_META[status];
          return (
            <div className="row" key={status} style={{ marginTop: 8 }}>
              <div className="col-md-3">
                <Tag
                  color={meta.color}
                  icon={<FontAwesomeIcon icon={meta.icon} className="generalStyles__mrFix" />}
                >
                  {cognitoUserStatusLabel(status)}
                </Tag>
              </div>
              <div className="col-md-9">
                <span className="drawer__text">{meta.description}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UserStatusLegend;
